import { Button } from "@/components/ui/button";
import {
  Circle,
  Eraser,
  Hand,
  Image,
  IterationCcw,
  IterationCw,
  MousePointer2,
  Pencil,
  SquareIcon,
  Triangle,
  Type,
} from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import useWhiteboard from "../../../hooks/useWhiteboard";
import { useAppSelector } from "../../../store/hooks";
import { userRoles } from "../../../store/slices/storeSlice";
import useCheckIsMobile from "../lessonRefactor/utils/useCheckIsMobile";
import styles from "./TutorLiveScreen.module.css";

const SessionCanvas = ({ sessionId }: { sessionId: number }) => {
  const { user } = useAppSelector((state) => state.store);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTool, setCurrentTool] = useState("pen");
  const [currentShape, setCurrentShape] = useState<
    "rectangle" | "circle" | "triangle"
  >("rectangle");
  const [currentStroke, setCurrentStroke] = useState<2 | 4 | 6>(2);
  const [currentColor, setCurrentColor] = useState<[number, number, number]>([
    0, 0, 0,
  ]);
  const {
    selectTool,
    penTool,
    handTool,
    // zoom,
    textTool,
    shapeTool,
    eraserTool,
    undo,
    redo,
    // reset,
    // takeScreenshot,
    imageTool,
    colorPicker,
    fontSizePicker,
    insertShape,
    resizeElement,
  } = useWhiteboard(canvasRef, setCurrentTool, sessionId);

  const getColorName = ([r, g, b]: [number, number, number]) => {
    if (r === 0 && g === 0 && b === 0) return "black";
    if (r === 255 && g === 0 && b === 0) return "red";
    if (r === 0 && g === 72 && b === 255) return "blue";
    if (r === 0 && g === 185 && b === 37) return "green";
    if (r === 249 && g === 250 && b === 251) return "white";
    return "black";
  };

  useEffect(() => {
    colorPicker(currentColor);
    insertShape(currentShape);
    fontSizePicker(currentStroke);
  }, [currentColor, currentShape, currentStroke]);

  const { isMobile } = useCheckIsMobile();
  return (
    <div id="canvas-container" className={styles.canvasContainer}>
      {user.role === userRoles.TEACHER && (
        <>
          {currentTool === "select" && isMobile && (
            <>
              <div
                className="flex flex-col rounded-md"
                style={{ backgroundColor: "#ddd" }}
              >
                <Button
                  variant="link"
                  className="px-2 py-0 mb-2 font-bold no-underline"
                  onClick={() => resizeElement(10, "x")}
                >
                  +
                </Button>
                <span className="mx-1 p-1 bg-white rounded-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-arrows-expand-vertical mx-auto"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5M.146 8.354a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 7.5H5.5a.5.5 0 0 1 0 1H1.707l1.147 1.146a.5.5 0 0 1-.708.708zM10 8a.5.5 0 0 1 .5-.5h3.793l-1.147-1.146a.5.5 0 0 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L14.293 8.5H10.5A.5.5 0 0 1 10 8" />
                  </svg>
                </span>
                <Button
                  variant="link"
                  className="btn px-2 py-0 mt-1 font-bold"
                  onClick={() => resizeElement(-10, "x")}
                >
                  -
                </Button>
              </div>

              <span className="mx-1 my-2 block border-t border-t-gray-400"></span>

              <div
                className="flex flex-col rounded-md"
                style={{ backgroundColor: "#ddd" }}
              >
                <Button
                  variant="link"
                  className="btn px-2 py-0 mb-2 font-bold"
                  onClick={() => resizeElement(10, "y")}
                >
                  +
                </Button>
                <span className="mx-1 p-1 bg-white rounded-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="black"
                    className="bi bi-arrows-expand mx-auto"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8M7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10"
                    />
                  </svg>
                </span>
                <Button
                  variant="link"
                  className="btn px-2 py-0 mt-1 font-bold"
                  onClick={() => resizeElement(-10, "y")}
                >
                  -
                </Button>
              </div>
            </>
          )}
          {["pen", "shape", "text"].includes(currentTool) && (
            <div className={styles.canvasSubToolbar}>
              {
                <>
                  <Button
                    variant="link"
                    className={`btn m-0 ${
                      getColorName(currentColor) === "white" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentColor([249, 250, 251])}
                  >
                    <span
                      style={{
                        backgroundColor: "#f9fbfa",
                        aspectRatio: "1/1",
                        border: "1px solid #c8c8c8",
                      }}
                    ></span>
                  </Button>
                  <Button
                    variant="link"
                    className={`btn m-0 ${
                      getColorName(currentColor) === "black" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentColor([0, 0, 0])}
                  >
                    <span
                      style={{
                        backgroundColor: "rgb(0, 0, 0)",
                        aspectRatio: "1/1",
                      }}
                    ></span>
                  </Button>
                  <Button
                    variant="link"
                    className={`btn m-0 ${
                      getColorName(currentColor) === "red" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentColor([255, 0, 0])}
                  >
                    <span
                      style={{
                        backgroundColor: "rgb(255, 0, 0)",
                        aspectRatio: "1/1",
                      }}
                    ></span>
                  </Button>
                  <Button
                    variant="link"
                    className={`btn m-0 ${
                      getColorName(currentColor) === "blue" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentColor([0, 72, 255])}
                  >
                    <span
                      style={{
                        backgroundColor: "rgb(0, 72, 255)",
                        aspectRatio: "1/1",
                      }}
                    ></span>
                  </Button>
                  <Button
                    variant="link"
                    className={`btn m-0 ${
                      getColorName(currentColor) === "green" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentColor([0, 185, 37])}
                  >
                    <span
                      style={{
                        backgroundColor: "rgb(0, 185, 37)",
                        aspectRatio: "1/1",
                      }}
                    ></span>
                  </Button>

                  <span className="mx-1 my-2 block border-t border-t-gray-400"></span>
                  <button
                    className={`btn m-0 p-1 my-1 ${
                      currentStroke === 2 ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentStroke(2)}
                  >
                    <span
                      style={{
                        borderTop: "2px solid black",
                        borderRadius: 0,
                        padding: 0,
                        margin: 0,
                      }}
                    ></span>
                  </button>
                  <button
                    className={`btn m-0 p-1 my-1 ${
                      currentStroke === 4 ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentStroke(4)}
                  >
                    <span
                      style={{
                        borderTop: "4px solid black",
                        borderRadius: 0,
                        padding: 0,
                        margin: 0,
                      }}
                    ></span>
                  </button>
                  <button
                    className={`btn m-0 p-1 my-1 ${
                      currentStroke === 6 ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentStroke(6)}
                  >
                    <span
                      style={{
                        borderTop: "6px solid black",
                        borderRadius: 0,
                        padding: 0,
                        margin: 0,
                      }}
                    ></span>
                  </button>
                </>
              }

              {currentTool === "shape" && (
                <>
                  <span className="mx-1 my-2 block border-t border-t-gray-400"></span>
                  <Button
                    variant="link"
                    className={`btn p-2 m-0 ${
                      currentShape === "rectangle" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentShape("rectangle")}
                  >
                    <SquareIcon className="text-primary" />
                  </Button>
                  <Button
                    variant="link"
                    className={`btn p-2 m-0 ${
                      currentShape === "circle" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentShape("circle")}
                  >
                    <Circle className="text-primary" />
                  </Button>
                  <Button
                    variant="link"
                    className={`btn p-2 m-0 ${
                      currentShape === "triangle" ? "btn-grey" : ""
                    }`}
                    onClick={() => setCurrentShape("triangle")}
                  >
                    <Triangle className="text-primary" />
                  </Button>
                </>
              )}
            </div>
          )}
          <div className={styles.canvasToolbar}>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "select"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={selectTool}
            >
              <MousePointer2 />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "pen" ? "bg-primary text-white" : "text-primary"
              }`}
              onClick={() => {
                penTool();
                fontSizePicker(currentStroke);
              }}
            >
              <Pencil />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "hand"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={handTool}
            >
              <Hand />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "eraser"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={() => {
                eraserTool();
                fontSizePicker(2);
              }}
            >
              <Eraser />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "text"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={() => {
                textTool();
                fontSizePicker(currentStroke);
              }}
            >
              <Type />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "shape"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={() => {
                shapeTool();
                fontSizePicker(currentStroke);
              }}
            >
              <SquareIcon />
            </Button>
            <Button
              variant="link"
              className={`btn py-1 px-2 m-0 ${
                currentTool === "image"
                  ? "bg-primary text-white"
                  : "text-primary"
              }`}
              onClick={imageTool}
            >
              <Image />
            </Button>
            <Button variant="link" className="btn py-1 px-2 m-0" onClick={undo}>
              <IterationCcw className="rotate-180" />
            </Button>
            <Button variant="link" className="btn py-1 px-2 m-0" onClick={redo}>
              <IterationCw className="rotate-180" />
            </Button>
            {/* <Button
                      variant="link" className="btn py-1 px-2 m-0" onClick={reset}>
          <i className="bi bi-arrow-repeat fs-5"></i>
        </Button> */}
            {/* <Button
              variant="link"
              className="btn py-1 px-2 m-0"
              onClick={() => zoom("in")}
            >
              <ZoomIn />
            </Button>
            <Button
              variant="link"
              className="btn py-1 px-2 m-0"
              onClick={() => zoom("out")}
            >
              <ZoomOut />
            </Button> */}
          </div>
        </>
      )}
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

export default memo(SessionCanvas);
